import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Input } from "./ui/input";
import { Loader2, MapPin, X } from "lucide-react";
import COMUNI from "../lib/comuni";
import { addressCase } from "../lib/addressCase";

const provDaComune = (nome) => {
  const nl = (nome || "").trim().toLowerCase();
  return (nl && COMUNI.find((c) => c.nome.toLowerCase() === nl)?.provincia) || "";
};

// Autocomplete indirizzo tramite backend /api/geo/autocomplete (Photon + Nominatim, dati OpenStreetMap).
// I risultati arrivano già normalizzati dal backend:
//   { indirizzo, comune, cap, provincia, lat, lon, display }
// Alla selezione richiama onPick con questi componenti (campi mancanti = stringa vuota).

export default function AddressAutocomplete({ value, onChange, onPick, city, prov, testId, placeholder, inputClassName = "h-9 bg-slate-900/60 border-slate-700" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(() => !!value); // dopo la selezione: modifica manuale senza riavviare la ricerca
  const ref = useRef(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Fallback lato browser: Photon (OSM) direttamente, se il backend non risponde o non ha risultati.
  const photonBrowser = async (q, cityName) => {
    try {
      const query = cityName ? `${q}, ${cityName}` : q;
      const r = await fetch(`https://photon.komoot.io/api/?${new URLSearchParams({ q: query, lang: "default", limit: "10" })}`);
      if (!r.ok) return [];
      const feats = (await r.json())?.features || [];
      const out = [];
      const seen = new Set();
      for (const f of feats) {
        const p = f.properties || {};
        if ((p.countrycode || "").toUpperCase() !== "IT") continue;
        const road = p.street || (p.osm_key === "highway" ? p.name : "");
        if (!road) continue;
        const indirizzo = [road, p.housenumber].filter(Boolean).join(" ");
        const comune = p.city || p.town || p.village || p.municipality || p.district || p.county || "";
        const cap = p.postcode || "";
        const coords = (f.geometry || {}).coordinates || [];
        const key = `${indirizzo.toLowerCase()}|${(comune || "").toLowerCase()}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ indirizzo, comune, cap, provincia: provDaComune(comune), lat: coords[1] ?? null, lon: coords[0] ?? null,
          display: [indirizzo, comune, cap].filter(Boolean).join(", ") });
      }
      if (cityName) { const cl = cityName.toLowerCase(); out.sort((a, b) => ((a.comune || "").toLowerCase() === cl ? 0 : 1) - ((b.comune || "").toLowerCase() === cl ? 0 : 1)); }
      return out.slice(0, 10);
    } catch { return []; }
  };

  const search = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 3) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true); setOpen(true);
      const cityName = (city || "").trim();
      let results = [];
      try {
        const { data } = await api.get("/geo/autocomplete", { params: { q: q.trim(), city: cityName } });
        if (Array.isArray(data)) results = data;
      } catch { results = []; }
      if (!results.length) { results = await photonBrowser(q.trim(), cityName); }
      setItems(results);
      setLoading(false);
    }, 400);
  };

  const pick = (it) => {
    onChange(it.indirizzo || it.display || "");
    onPick && onPick(it);
    setOpen(false); setItems([]); setPicked(true);
  };

  const reset = () => {
    if (timer.current) clearTimeout(timer.current);
    onChange("");
    setItems([]); setOpen(false); setPicked(false);
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div className="relative w-full" ref={ref}>
      <MapPin className="h-4 w-4 text-amber-500/80 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none z-10" />
      <Input ref={inputRef} value={value || ""} noUppercase
        onChange={(e) => { onChange(e.target.value); if (!picked) search(e.target.value); }}
        onBlur={(e) => { const c = addressCase(e.target.value); if (c && c !== e.target.value) onChange(c); }}
        onFocus={() => { if (!picked && items.length) setOpen(true); }}
        data-testid={testId} className={`${inputClassName} w-full pl-9 ${value ? "pr-9" : "pr-3"}`}
        placeholder={placeholder || "Via, piazza… (suggerimenti reali)"} autoComplete="off" />
      {value && (
        <button type="button" onClick={reset} data-testid={`${testId}-reset`} title="Reimposta e cerca di nuovo"
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 text-slate-400 hover:text-amber-400 rounded-full p-0.5 hover:bg-slate-700/60 transition-colors">
          <X className="h-4 w-4" />
        </button>
      )}
      {open && !picked && (loading || items.length > 0) && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-56 overflow-y-auto" data-testid={`${testId}-list`}>
          {loading && <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca indirizzi…</div>}
          {items.map((it, i) => (
            <button key={i} type="button" onMouseDown={(e) => { e.preventDefault(); pick(it); }} data-testid={`${testId}-opt`}
              className="w-full text-left px-3 py-2 text-xs text-slate-200 hover:bg-slate-800/60 border-b border-slate-800 last:border-0 leading-snug">
              {it.display || it.indirizzo}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
